import { useState, useEffect } from 'react';
import { getAccessToken } from '../utils/auth';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

function AgentTables({ agent, onBack }) {
  const [tablesData, setTablesData] = useState({});
  const [selectedTable, setSelectedTable] = useState('');
  const [queryText, setQueryText] = useState('');
  const [queryResult, setQueryResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [limitValue, setLimitValue] = useState(100);
  const [groupByColumn, setGroupByColumn] = useState('');
  const [aggregateByColumn, setAggregateByColumn] = useState('');
  const [showGroupByDropdown, setShowGroupByDropdown] = useState(false);
  const [showAggregateByDropdown, setShowAggregateByDropdown] = useState(false);
  const [aggregateFunction, setAggregateFunction] = useState('count');
  const [showFunctionDropdown, setShowFunctionDropdown] = useState(false);
  const [aggregatedData, setAggregatedData] = useState(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportFileName, setExportFileName] = useState('');

  useEffect(() => {
    if (groupByColumn && aggregateByColumn && queryResult && queryResult.type === 'dataframe' && Array.isArray(queryResult.result) && queryResult.result.length > 0) {
      try {
        // Manual aggregation without danfojs
        const grouped = {};
        queryResult.result.forEach(row => {
          const key = row[groupByColumn];
          if (!grouped[key]) {
            grouped[key] = [];
          }
          grouped[key].push(row[aggregateByColumn]);
        });

        const resultData = Object.keys(grouped).sort().map(key => {
          const values = grouped[key].filter(v => v != null && !isNaN(v));
          let aggregatedValue;
          if (aggregateFunction === 'count') {
            aggregatedValue = grouped[key].length;
          } else if (aggregateFunction === 'sum') {
            aggregatedValue = values.reduce((a, b) => a + b, 0);
          } else if (aggregateFunction === 'mean') {
            aggregatedValue = values.length > 0 ? values.reduce((a, b) => a + b, 0) / values.length : 0;
          } else if (aggregateFunction === 'max') {
            aggregatedValue = values.length > 0 ? Math.max(...values) : null;
          } else if (aggregateFunction === 'min') {
            aggregatedValue = values.length > 0 ? Math.min(...values) : null;
          }
          return { [groupByColumn]: key, [aggregateByColumn]: aggregatedValue };
        });
        setAggregatedData(resultData);
      } catch (error) {
        console.error('Aggregation error:', error);
        setAggregatedData(null);
      }
    } else {
      setAggregatedData(null);
    }
  }, [groupByColumn, aggregateByColumn, queryResult, aggregateFunction]);

  const exportToJson = () => {
    const sanitized = queryResult.result.map(row =>
      Object.fromEntries(Object.entries(row).map(([k, v]) => [k.replace(/ /g, '_'), v]))
    );
    const blob = new Blob([JSON.stringify(sanitized, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${exportFileName || selectedTable}.json`;
    a.click();
    URL.revokeObjectURL(url);
    setShowExportModal(false);
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortedData = () => {
    const entries = Object.entries(tablesData);
    if (!sortConfig.key) return entries;

    return entries.sort((a, b) => {
      let aVal, bVal;
      if (sortConfig.key === 'name') {
        aVal = a[0];
        bVal = b[0];
      } else if (sortConfig.key === 'fields') {
        aVal = Array.isArray(a[1].columns) ? a[1].columns.join('\n') : a[1].columns;
        bVal = Array.isArray(b[1].columns) ? b[1].columns.join('\n') : b[1].columns;
      } else if (sortConfig.key === 'records') {
        aVal = a[1].records;
        bVal = b[1].records;
      } else if (sortConfig.key === 'partitions') {
        aVal = a[1].partitions;
        bVal = b[1].partitions;
      }

      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
      }
      
      const aStr = String(aVal || '');
      const bStr = String(bVal || '');
      return sortConfig.direction === 'asc' ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
    });
  };

  useEffect(() => {
    const fetchTablesMetadata = async () => {
      const token = await getAccessToken();
      if (token) {
        fetch(`${process.env.REACT_APP_TABLES_API_EP}/knowledgebase/${agent.id}/partitions/tables/metadata`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })
          .then(response => response.json())
          .then(data => {
            setTablesData(data);
            const firstTable = Object.keys(data)[0];
            if (firstTable) {
              setSelectedTable(firstTable);
              console.log('Selected table:', firstTable);
            }
          })
          .catch(error => console.error('Error fetching tables metadata:', error));
      }
    };
    fetchTablesMetadata();
  }, [agent.id]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.dropdown')) {
        setShowGroupByDropdown(false);
        setShowAggregateByDropdown(false);
        setShowFunctionDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className='edit-page'>
      <div className="header-layout">
        <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
          <img 
            src="/media/navigate-left.png" 
            alt="Back" 
            className="icon-medium" 
            onClick={() => onBack && onBack(agent.id)}
            style={{cursor: 'pointer'}}
          />
          <h2 className="header-title">Tables: {agent.display_name}</h2>
        </div>
      </div>
      <div className="edit-form">
        <div className="agent-tables-row-1">
          <div className="agent-tables-row-1-col-1">
            <div className="agent-tables-metadata-section">
              <table id="agent-tables-metadata-table" className="data-table">
                <thead>
                  <tr>
                    <th data-column="generic"></th>
                    <th data-column="generic" onClick={() => handleSort('name')} style={{cursor: 'pointer'}}>Name {sortConfig.key === 'name' && (sortConfig.direction === 'asc' ? '▲' : '▼')}</th>
                    <th data-column="generic" onClick={() => handleSort('fields')} style={{cursor: 'pointer'}}>Fields {sortConfig.key === 'fields' && (sortConfig.direction === 'asc' ? '▲' : '▼')}</th>
                    <th data-column="generic" onClick={() => handleSort('records')} style={{cursor: 'pointer', textAlign: 'right'}}>Rows {sortConfig.key === 'records' && (sortConfig.direction === 'asc' ? '▲' : '▼')}</th>
                    <th data-column="generic" onClick={() => handleSort('partitions')} style={{cursor: 'pointer', textAlign: 'right'}}>Partitions {sortConfig.key === 'partitions' && (sortConfig.direction === 'asc' ? '▲' : '▼')}</th>
                  </tr>
                </thead>
                <tbody>
                  {getSortedData().map(([id, data]) => (
                    <tr key={id} className="agent-tables-row" onClick={() => {
                      setSelectedTable(id);
                      console.log('Selected table:', id);
                    }}>
                      <td data-column="generic" style={{textAlign: 'center'}}>
                        <input 
                          id={`table-radio-${id}`}
                          type="radio" 
                          name="table-selection"
                          checked={selectedTable === id}
                          onChange={() => {
                            setSelectedTable(id);
                            console.log('Selected table:', id);
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </td>
                      <td>{data.name}</td>
                      <td className="agent-tables-columns">{Array.isArray(data.columns) ? data.columns.join('\n') : data.columns}</td>
                      <td style={{textAlign: 'right'}}>{data.records}</td>
                      <td style={{textAlign: 'right'}}>{data.partitions}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
        
          <div className="agent-tables-query-section">
            <label htmlFor="query-textarea">Query selected table using natural language:</label>
            <br />
            <textarea 
              id="query-textarea"
              rows={4}
              className="agent-tables-query-textarea"
              value={queryText}
              onChange={(e) => setQueryText(e.target.value)}
              onKeyDown={async (e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  const token = await getAccessToken();
                  // if (token && queryText.trim()) {
                  // if (token && queryText.trim() && selectedTable) {
                  if (token && selectedTable) {
                    setIsLoading(true);
                    setQueryResult(null);
                    const limitParam = limitValue >= 0 ? `&limit=${limitValue}` : '';
                    fetch(`${process.env.REACT_APP_TABLES_API_EP}/knowledgebase/${agent.id}/partitions/tables/query?table_id=${encodeURIComponent(selectedTable)}&q=${encodeURIComponent(queryText)}${limitParam}`, {
                      headers: { 'Authorization': `Bearer ${token}` }
                    })
                      .then(response => {
                        console.log('Query result with response status: ', response.status)
                        if (!response.ok) {
                          return response.json().then(errorData => {
                            throw new Error(errorData.message || 'Query failed');
                          });
                        }
                        return response.json();
                      })
                      .then(data => {
                        setQueryResult(data);
                        setIsLoading(false);
                      })
                      .catch(error => {
                        setQueryResult({ type: 'error', result: error.message });
                        setIsLoading(false);
                      });
                  }
                }
              }}
            />
            <br />
            <label htmlFor="limit-input">Default limit when query is empty, enter 0 for no limit (all records):</label>
            <br />
            <input 
              id="limit-input"
              type="number"
              min="0"
              value={limitValue}
              onChange={(e) => setLimitValue(Math.max(0, parseInt(e.target.value) || 0))}
              style={{width: '100px', padding: '8px', marginTop: '5px'}}
            />
          </div>
        </div>
        
        {queryResult && queryResult.type === 'dataframe' && Array.isArray(queryResult.result) && queryResult.result.length > 0 && (
          <div className="agent-tables-row-1-col-2">
            <h3>Data Aggregation</h3>
            <div className="form-field dropdown agent-tables-groupby-dropdown">
              <label className="form-label">Group by</label>
              <div 
                onClick={() => setShowGroupByDropdown(!showGroupByDropdown)}
                className="dropdown-trigger"
              >
                {groupByColumn || 'Select column'}
              </div>
              {showGroupByDropdown && (
                <div className="dropdown-menu">
                  {Object.keys(queryResult.result[0]).map((key) => (
                    <div 
                      key={key} 
                      onClick={() => { 
                        setGroupByColumn(key);
                        setShowGroupByDropdown(false);
                      }}
                      className="dropdown-option"
                    >
                      {key}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="form-field dropdown agent-tables-aggregateby-dropdown">
              <label className="form-label">Aggregate by</label>
              <div 
                onClick={() => setShowAggregateByDropdown(!showAggregateByDropdown)}
                className="dropdown-trigger"
              >
                {aggregateByColumn || 'Select column'}
              </div>
              {showAggregateByDropdown && (
                <div className="dropdown-menu">
                  {Object.keys(queryResult.result[0]).map((key) => (
                    <div 
                      key={key} 
                      onClick={() => { 
                        setAggregateByColumn(key);
                        setShowAggregateByDropdown(false);
                      }}
                      className="dropdown-option"
                    >
                      {key}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="form-field dropdown agent-tables-function-dropdown">
              <label className="form-label">Function</label>
              <div 
                onClick={() => setShowFunctionDropdown(!showFunctionDropdown)}
                className="dropdown-trigger"
              >
                {aggregateFunction}
              </div>
              {showFunctionDropdown && (
                <div className="dropdown-menu">
                  {['count', 'sum', 'mean', 'max', 'min'].map((func) => (
                    <div 
                      key={func} 
                      onClick={() => { 
                        setAggregateFunction(func);
                        setShowFunctionDropdown(false);
                      }}
                      className="dropdown-option"
                    >
                      {func}
                    </div>
                  ))}
                </div>
              )}
            </div>
            {aggregatedData && aggregatedData.length > 0 && (
              <div className="ag-theme-alpine" style={{ height: '400px', width: '100%', marginTop: '20px' }}>
                <AgGridReact
                  rowData={aggregatedData}
                  columnDefs={Object.keys(aggregatedData[0]).map(key => ({
                    field: key,
                    headerName: key,
                    sortable: true,
                    filter: true,
                    resizable: true
                  }))}
                  defaultColDef={{
                    flex: 1,
                    minWidth: 100
                  }}
                />
              </div>
            )}
          </div>
        )}
        </div>
        
        <div className="agent-tables-row-2">
          {isLoading && (
            <div className="agent-tables-query-result">
              <img src="/media/spinner3.gif" alt="Loading..." className="agent-tables-spinner" />
            </div>
          )}
          
          {queryResult && !isLoading && (
            <div id="query_result" className="agent-tables-query-result">
              {queryResult.type === 'plot' || queryResult.type === 'chart' ? (
                <img src={queryResult.result.image_base64} alt="Unable to display the received image!" />
              ) : queryResult.type === 'number' || queryResult.type === 'string' ? (
                <div>{queryResult.result}</div>
              ) : queryResult.type === 'error' ? (
                <div className="agent-tables-error">{queryResult.result}</div>
              ) : queryResult.type === 'dataframe' && Array.isArray(queryResult.result) && queryResult.result.length > 0 ? (
                <div>
                  <button onClick={() => { setExportFileName(selectedTable); setShowExportModal(true); }} style={{ marginBottom: '8px' }}>Export JSON</button>
                  <div className="ag-theme-alpine" style={{ height: 'calc(100vh - 900px)', width: '95%' }}>
                  <AgGridReact
                    rowData={queryResult.result}
                    columnDefs={Object.keys(queryResult.result[0]).map(key => ({
                      field: key,
                      headerName: key,
                      sortable: true,
                      filter: true,
                      resizable: true,
                      wrapText: true,
                      autoHeight: true
                    }))}
                    defaultColDef={{
                      flex: 1,
                      minWidth: 100
                    }}
                    pagination={true}
                    paginationPageSize={50}
                  />
                  </div>
                </div>
              ) : null}
            </div>
          )}
        </div>
      </div>
      {showExportModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Export JSON</h3>
            <label>File name:</label>
            <input
              type="text"
              value={exportFileName}
              onChange={(e) => setExportFileName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && exportToJson()}
              style={{ width: '100%', padding: '8px', margin: '8px 0' }}
              autoFocus
            />
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowExportModal(false)}>Cancel</button>
              <button onClick={exportToJson}>Export</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AgentTables;
